
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { testimonials } from '@/components/Testimonials';

const TestimonialsPage = () => {
  const { toast } = useToast();
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [userReview, setUserReview] = useState<null | { text: string; rating: number }>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUserReview({ text: review, rating });
    setReview('');
    setRating(5);
    toast({
      title: "Review submitted",
      description: "Thank you for your feedback!",
    });
  };

  return (
    <div className="min-h-screen bg-[#222222] py-16">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold mb-12 text-center text-accent">Client Testimonials</h1>
        
        {/* Review submission form */}
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-lg mb-12">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">Submit Your Review</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 cursor-pointer ${
                      star <= rating ? 'text-accent fill-current' : 'text-gray-300'
                    }`}
                    onClick={() => setRating(star)}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
              <Textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder="Share your experience..."
                className="min-h-[100px]"
              />
            </div>
            <Button type="submit" className="w-full">Submit Review</Button>
          </form>
        </div>

        {/* User's submitted review */}
        {userReview && (
          <div className="max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl font-semibold mb-4 text-accent">Your Review</h2>
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex mb-2">
                {[...Array(userReview.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-accent fill-current" />
                ))}
              </div>
              <p className="text-gray-700">{userReview.text}</p>
            </div>
          </div>
        )}

        {/* All testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-accent fill-current" />
                ))}
              </div>
              <p className="text-gray-700 mb-4">{testimonial.text}</p>
              <div>
                <p className="font-semibold text-gray-900">{testimonial.name}</p>
                <p className="text-sm text-gray-500">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>

        {/* SEO Content */}
        <div className="max-w-4xl mx-auto text-gray-400 space-y-6 text-sm leading-relaxed mb-24">
          <p>Looking for reliable appliance repairs in Toronto? Our expert technicians provide fast, professional service across the GTA. From emergency washer repair to comprehensive dryer maintenance, we're your trusted solution for all appliance needs. Our certified team specializes in residential and commercial appliance repairs throughout Toronto and the surrounding areas.</p>
          
          <p>As Toronto's leading appliance repair specialists, we offer same-day emergency service for all major brands and models. Whether you need urgent fridge repair in the GTA or professional oven maintenance in Toronto, our experienced technicians are ready to help. We pride ourselves on delivering quick, efficient solutions for all your appliance troubles.</p>
          
          <p>Our services cover everything from dishwasher fixes in the GTA to stove maintenance in Toronto. With years of experience in freezer repair and appliance troubleshooting, our certified professionals ensure your kitchen and laundry appliances run smoothly. We understand the importance of reliable appliance care and provide affordable solutions for all your repair needs.</p>
          
          <p>Need emergency appliance repair in Toronto? Our team offers 24/7 service for urgent repairs. From commercial kitchen equipment to residential laundry machines, we handle all types of appliance maintenance. Our energy-efficient repair solutions help save on utility bills while extending your appliance's lifespan.</p>
          
          <p>When it comes to kitchen appliance repair in the GTA, trust our experienced technicians for professional service. We specialize in refrigerator repair, oven maintenance, and comprehensive stove repair solutions. Our affordable rates and same-day service make us the preferred choice for appliance repair in Toronto.</p>
          
          <p>Looking for washer repair services in Toronto? Our certified technicians provide expert diagnosis and repair for all washing machine brands. We also offer professional dryer repair, ensuring your laundry appliances work efficiently. From simple maintenance to complex repairs, we've got you covered.</p>
          
          <p>For reliable dishwasher repair in the GTA, count on our experienced team. We handle everything from basic maintenance to complex repairs, ensuring your kitchen appliances run smoothly. Our affordable rates and professional service make us Toronto's trusted choice for appliance care.</p>
          
          <p>Need freezer repair in Toronto? Our certified technicians provide fast, efficient service for all freezer models. We understand the urgency of freezer repairs and offer same-day service to prevent food spoilage. Trust our experts for professional freezer maintenance and repair.</p>
          
          <p>As Toronto's trusted appliance repair specialists, we offer comprehensive services for all major appliance brands. From emergency repairs to routine maintenance, our certified technicians deliver reliable solutions. We pride ourselves on providing affordable, professional service across the GTA.</p>
          
          <p>For commercial appliance repair in Toronto, our experienced team handles all types of equipment. We understand the importance of minimizing downtime and offer quick, efficient repair solutions. Trust our certified technicians for professional appliance care.</p>
          
          <p>Looking for energy-efficient appliance solutions? Our expert technicians provide professional advice and repairs to optimize your appliance performance. We specialize in eco-friendly repairs and maintenance, helping you save on energy costs while protecting the environment.</p>
          
          <p>Need same-day appliance repair in the GTA? Our professional technicians are available for emergency service calls. From urgent refrigerator repairs to immediate oven fixes, we provide quick, reliable solutions for all your appliance needs.</p>
          
          <p>For professional laundry appliance repair in Toronto, trust our certified technicians. We handle washer and dryer repairs, maintenance, and installation. Our comprehensive service ensures your laundry appliances work efficiently and reliably.</p>
          
          <p>When it comes to kitchen appliance troubleshooting in the GTA, our experienced team provides expert diagnosis and repair. We handle all major brands and models, offering professional solutions for your appliance needs. Trust Toronto's leading appliance repair specialists for reliable service.</p>
          
          <p>Need affordable appliance repair in Toronto? Our competitive rates and professional service make us the preferred choice for residential and commercial repairs. From routine maintenance to emergency fixes, we provide cost-effective solutions for all your appliance needs.</p>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsPage;
